import { type IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type ReactNode } from 'react';
import { tv } from 'tailwind-variants';

import { cn } from '../lib/utils';

// ── Types ─────────────────────────────────────────────────────────────────────

type IconItemColor = 'primary' | 'secondary' | 'destructive' | 'warning' | 'neutral';

interface IconItemProps {
  checked?: boolean;
  label?: ReactNode;
  icon?: IconDefinition;
  color?: IconItemColor;
  size?: 'sm' | 'md' | 'lg';
  children?: ReactNode;
  className?: string;
  dataTestId?: string;
}

// ── Variants ──────────────────────────────────────────────────────────────────

const iconItemVariants = tv({
  slots: {
    root: 'flex items-start',
    icon: 'shrink-0',
    text: 'grow'
  },
  variants: {
    size: {
      sm: { root: 'gap-1.5', icon: 'text-sm mt-px', text: 'text-sm' },
      md: { root: 'gap-2', icon: 'text-base mt-0.5', text: 'text-sm' },
      lg: { root: 'gap-2.5', icon: 'text-lg mt-0.5', text: 'text-base' }
    }
  },
  defaultVariants: {
    size: 'md'
  }
});

// ── Color map ─────────────────────────────────────────────────────────────────

const colorClassMap: Record<IconItemColor, string> = {
  primary: 'text-green-600 dark:text-green-400',
  secondary: 'text-blue-600 dark:text-blue-400',
  destructive: 'text-uniform-red-600 dark:text-uniform-red-400',
  warning: 'text-yellow-700 dark:text-yellow-400',
  neutral: 'text-uniform-gray-500 dark:text-uniform-gray-400'
};

// ── Component ─────────────────────────────────────────────────────────────────

function IconItem({
  checked = false,
  label,
  icon,
  color,
  size = 'md',
  children,
  className,
  dataTestId
}: IconItemProps) {
  const resolvedIcon = icon ?? (checked ? faCheckCircle : faTimesCircle);
  const resolvedColor: IconItemColor = color ?? (checked ? 'primary' : 'destructive');

  const { root, icon: iconSlot, text } = iconItemVariants({ size });

  return (
    <div className={cn(root(), className)}>
      <FontAwesomeIcon
        icon={resolvedIcon}
        data-testid={dataTestId}
        className={cn(iconSlot(), colorClassMap[resolvedColor])}
      />
      <span className={text()}>
        {label}
        {children}
      </span>
    </div>
  );
}

export { IconItem, iconItemVariants };
export type { IconItemProps, IconItemColor };
